import React from 'react';
import { observer } from 'mobx-react';
import { CircularProgress, Card, Grid } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { connect } from 'formik';
import { getSnapshot } from 'mobx-state-tree';

import { useStore } from 'core/store';
import { useQuery, gql } from '@apollo/client';
import useErrors from 'common/hooks/use-errors';
import useSession from 'core/hooks/use-session';
import { isBranchEnv } from 'brand-config';
import { buildBixAutoUpsellInput } from 'common/components/branch-auto-upsell';
import { InternalUwNotes } from 'common/components/internal-uw-notes';
import { RecalculatingDim } from '../recalculating-dim';
import { PolicyErrors } from './components/policy-errors';
import { Additionals } from './components/additionals';
import { DiscountsCard } from './components/discounts-card';
import { AdditionalOffersSection } from './components/additional-offers-section';
import { PriceDetailsCard } from './components/price-details-card';
import { PolicyPickerCard } from './components/policy-picker-card';
import { DocumentsSharing } from './components/documents-sharing';
import { OfferTools } from './components/offer-tools';
import { THEME_VARIANTS } from 'core/components/theme/theme-variant';
import useStyles from './sidebar.styles';

// Reskin: related cards sit 8px apart inside a group and groups 24px apart, so spacing shows the grouping.
// Classic renders the cards straight into the sidebar, unchanged.
const SidebarGroup = ({ className, children }) => (className ? <div className={className}>{children}</div> : children);

const Sidebar = observer(() => {
  const classes = useStyles();
  const { themeVariant } = useTheme();
  const groupClassName = themeVariant === THEME_VARIANTS.RESKIN ? classes.group : undefined;
  const { offer: store } = useStore();
  const { isInternalAgent, viewOnly } = useSession();
  const getErrorsAndRejections = (code) => code >= 4003;
  const errorsAndRejections = useErrors(getSnapshot(store.errors), getErrorsAndRejections);

  const assurityLifeEligibilityQuery = useQuery(
    gql`
      query ($offerId: String) {
        assurityLifeCheckEligibility(args: { branchOfferId: $offerId }) {
          __typename
          ... on AssurityLifeCheckEligibilitySuccessResponse {
            staffUserCapabilities
            eligibleLifeOffers {
              benefitAmount
              monthlyPremiumAmount
              annualPremiumAmount
              personId
            }
          }
        }
      }
    `,
    { variables: { offerId: store?.offer?.id }, skip: !store?.offer?.id }
  );

  const shouldShowAssurityTermLifeAdditionalOffer = Boolean(
    !assurityLifeEligibilityQuery.loading &&
      assurityLifeEligibilityQuery.data?.assurityLifeCheckEligibility.__typename !==
        'AssurityLifeCheckEligibilityStaffUserNotEligibleResponse'
  );

  // BIX auto upsell — only on non-Branch carrier farm offers with a usable primary
  // applicant + farmLocations[0] address. Gated on `!viewOnly` so view-only operators
  // don't trigger the mount-time mutation. (We can't use `canEditOffers` here — that's
  // a Branch-staff role check; mutual carrier agents aren't in those groups.) The
  // agent-eligibility check (`branch_play = "Mutuals Accommodation"` lookup) lives
  // server-side in the `create-bix-auto-offer-upsell` lambda — see GitHub issue #27759.
  const bixAutoUpsellInput =
    !viewOnly && !isBranchEnv && store.isFarmOffer && store?.offer?.id
      ? buildBixAutoUpsellInput({
          branchOfferId: store.offer.id,
          applicant: store?.offer?.quote?.drivers?.find((d) => d.isPrimary),
          farmAddress: store?.offer?.quote?.farmLocations?.[0]?.address,
          email: store?.offer?.quote?.email,
          phone: store?.offer?.quote?.phone
        })
      : null;

  const shouldShowAdditionalOffers =
    (shouldShowAssurityTermLifeAdditionalOffer && !store.isFarmOffer) || !!bixAutoUpsellInput;

  const isLoading =
    (store.loading && !store.recalculatingPrice) || !store.offer || assurityLifeEligibilityQuery.loading;

  if (isLoading) {
    return (
      <>
        <div className={classes.columnHeader} />
        <Card className={classes.container}>
          <Grid container className={classes.loaderContainer} alignItems="center" justifyContent="center">
            <CircularProgress className={classes.loader} />
          </Grid>
        </Card>
      </>
    );
  }

  return (
    <RecalculatingDim id="offer_details" className={classes.sidebar}>
      <SidebarGroup className={groupClassName}>
        <div className={classes.columnHeader} />
        {/* Staff-only internal UW notes — self-gates on the session's isAgency flag; never shown to agents. #35208.
            Key on the sticky webUserId, not offer.id: offer.id is regenerated on every mutation
            (re-rate / approve-for-checkout), which orphaned the notes. Fall back to id. #35323 */}
        <InternalUwNotes offerId={store?.offer?.webUserId || store?.offer?.id} />
      </SidebarGroup>
      <SidebarGroup className={groupClassName}>
        <PolicyPickerCard />
        <PriceDetailsCard />
        {!store.isBranchPlusOnly && <DiscountsCard />}
      </SidebarGroup>
      {(isInternalAgent || store.shouldShowAdditionalPaymentsSection || shouldShowAdditionalOffers) && (
        <SidebarGroup className={groupClassName}>
          {isInternalAgent && <OfferTools />}
          {store.shouldShowAdditionalPaymentsSection && <Additionals />}
          {shouldShowAdditionalOffers && (
            <AdditionalOffersSection
              assurityTermLife={shouldShowAssurityTermLifeAdditionalOffer && !store.isFarmOffer}
              assurityTermLifeData={{
                ...assurityLifeEligibilityQuery.data?.assurityLifeCheckEligibility,
                branchOfferId: store?.offer?.id,
                email: store?.offer?.quote.email
              }}
              bixAutoUpsellInput={bixAutoUpsellInput}
            />
          )}
        </SidebarGroup>
      )}
      {errorsAndRejections.length > 0 && (
        <SidebarGroup className={groupClassName}>
          <PolicyErrors />
        </SidebarGroup>
      )}
      <SidebarGroup className={groupClassName}>
        <DocumentsSharing />
      </SidebarGroup>
    </RecalculatingDim>
  );
});

export default connect(Sidebar);
